import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import { Formik } from "formik";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { passwordRule, confirmationPasswordRule } from "@utils/validation";
import AuthenticationService from "@services/Authentication";

const useStyles = makeStyles((theme) => ({
  content: {
    paddingTop: "8px",
    overflow: "hidden",
  },
}));

function PasswordEditor(props) {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);

  function changePassword(values, callback) {
    if (loading) {
      return;
    }

    setLoading(true);

    AuthenticationService.changePassword(
      values.currentPassword,
      values.newPassword
    )
      .then(() => {
        toast.success("Senha alterada!");
        callback();
        setLoading(false);
        props.onSuccess();
      })
      .catch((e) => {
        console.error(e);

        if (e.code === "NotAuthorizedException") {
          toast.warning("Usuário e/ou senha inválidos!");
        } else {
          toast.error("Erro ao alterar senha!");
        }

        setLoading(false);
      });
  }

  function handleClickClose(callback) {
    callback();
    props.onClose();
  }

  function handleClickCancel(callback) {
    callback();
    props.onCancel();
  }

  function handleClickSubmit(values, callback) {
    changePassword(values, callback);
  }

  const initialValues = {
    currentPassword: "",
    newPassword: "",
    reenterNewPassword: "",
  };

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required("Campo requerido"),
    newPassword: passwordRule(),
    reenterNewPassword: confirmationPasswordRule("newPassword"),
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      isInitialValid={false}
    >
      {({
        errors,
        touched,
        isValid,
        resetForm,
        values,
        handleChange,
        handleBlur,
      }) => (
        <Dialog
          open={props.show}
          onClose={() => {
            handleClickClose(() => {
              resetForm();
            });
          }}
        >
          <DialogTitle>Alterar senha</DialogTitle>

          <DialogContent>
            <div className={classes.content}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    value={values.currentPassword}
                    helperText={
                      touched.currentPassword ? errors.currentPassword : ""
                    }
                    error={
                      touched.currentPassword && Boolean(errors.currentPassword)
                    }
                    variant="outlined"
                    required
                    fullWidth
                    name="currentPassword"
                    label="Senha Atual"
                    type="password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    value={values.newPassword}
                    helperText={touched.newPassword ? errors.newPassword : ""}
                    error={touched.newPassword && Boolean(errors.newPassword)}
                    variant="outlined"
                    required
                    fullWidth
                    name="newPassword"
                    label="Senha"
                    type="password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    value={values.reenterNewPassword}
                    helperText={
                      touched.reenterNewPassword
                        ? errors.reenterNewPassword
                        : ""
                    }
                    error={
                      touched.reenterNewPassword &&
                      Boolean(errors.reenterNewPassword)
                    }
                    variant="outlined"
                    required
                    fullWidth
                    name="reenterNewPassword"
                    label="Confirmação da Senha"
                    type="password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
              </Grid>
            </div>
          </DialogContent>

          <DialogActions>
            <Button
              color="default"
              disabled={loading}
              onClick={() => {
                handleClickCancel(() => {
                  resetForm();
                });
              }}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              disabled={!isValid || loading}
              onClick={() => {
                handleClickSubmit(values, () => {
                  resetForm();
                });
              }}
            >
              Alterar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Formik>
  );
}

PasswordEditor.defaultProps = {
  show: false,
  onClose: () => {},
  onCancel: () => {},
  onSuccess: () => {},
};

PasswordEditor.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
};

export default PasswordEditor;
